import {
  inject,
  importProvidersFrom,
  InjectionToken,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import { OAuthModule } from 'angular-oauth2-oidc';

import { ConfigService } from '../config/config.service';
import { AuthService } from './auth.service';
import { UserAccessService } from './user-access.service';

export interface AuthEnvironment {
  readonly name: string;
  readonly audience: string;
  readonly clientId: string;
  readonly issuer: string;
  readonly jwksPath: string;
  readonly scope: string;
}

export const AUTH_ENVIRONMENT = new InjectionToken<AuthEnvironment>('AUTH_ENVIRONMENT');

export function provideAuth() {
  return makeEnvironmentProviders([
    importProvidersFrom(OAuthModule.forRoot()),
    {
      provide: AUTH_ENVIRONMENT,
      useFactory: (configService: ConfigService): AuthEnvironment => ({
        name: configService.getEnv('name'),
        audience: configService.getEnv('idpAudience'),
        clientId: configService.getEnv('idpClientId'),
        issuer: configService.getEnv('idpIssuer'),
        jwksPath: configService.getEnv('idpPathJwks'),
        scope: configService.getEnv('idpScope'),
      }),
      deps: [ConfigService],
    },
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const userAccessService = inject(UserAccessService);
      const router = inject(Router);

      const params = new URLSearchParams(window.location.search);
      if (params.get('error') === 'access_denied') {
        void router.navigateByUrl('/idp-access-denied');
        return;
      }

      return authService.authorize().then(async (authorized) => {
        if (authorized) {
          await userAccessService.initialize(authService.profileForSync());
          void authService.checkSilentRefreshAvailability();
        }
      });
    }),
  ]);
}
